import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { ImageSEO } from "../../core/models/image-seo.model";
import { SiilaTranslationService } from "../../core/services/siila-translation.service";

export interface SpotCarouselEventData {
  pageContext: string;
  pageLanguage: string;
  propertyName: string;
  propertyId: string;
  cardPosition: number;
  spotAdvertiserName?: string;
  spotAdvertiserSlug?: string;
}

/**
 * A REsource article shown outside the REsource pages (the property page
 * "Featured REsource" block, the "Industry News" carousel). Reported with the
 * same `resource_banner_*` events as the advertiser banners.
 */
export interface ResourceArticleEventData {
  articleId: number | string;
  placement: string;
  position: number;
  pageContext: string;
}

type ResourceBannerEvent =
  | "resource_banner_impression"
  | "resource_banner_click";

/**
 * Articles have no advertiser of their own; they are SiiLA content, so they
 * report under the SiiLA brand instead of landing as "(not set)" in GA4.
 */
const RESOURCE_ARTICLE_ADVERTISER = { name: "SiiLA", slug: "siila" };

@Injectable()
export class AnalyticsService {
  constructor(private siilaTranslationService: SiilaTranslationService) {}

  public init() {
    if (
      environment.googleTrackingID &&
      environment.googleTrackingID.length > 0
    ) {
      this.configTrackingID(environment.googleTrackingID);
    }
  }

  trackBannerImpression(
    banner: ImageSEO,
    position: number,
    region: string,
    placement: string = "resource_banner",
    pageContext: string = ""
  ): void {
    this.pushBannerEvent(
      "resource_banner_impression",
      banner,
      position,
      region,
      placement,
      pageContext
    );
  }

  trackBannerClick(
    banner: ImageSEO,
    position: number,
    region: string,
    placement: string = "resource_banner",
    pageContext: string = ""
  ): void {
    this.pushBannerEvent(
      "resource_banner_click",
      banner,
      position,
      region,
      placement,
      pageContext
    );
  }

  trackResourceArticleImpression(data: ResourceArticleEventData): void {
    this.pushResourceArticleEvent("resource_banner_impression", data);
  }

  trackResourceArticleClick(data: ResourceArticleEventData): void {
    this.pushResourceArticleEvent("resource_banner_click", data);
  }

  trackSpotCarouselImpression(data: SpotCarouselEventData): void {
    if (typeof window === "undefined") return;
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(
      this.buildSpotCarouselPayload("spot_carousel_impression", data)
    );
  }

  trackSpotCarouselClick(data: SpotCarouselEventData): void {
    if (typeof window === "undefined") return;
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(
      this.buildSpotCarouselPayload("spot_carousel_click", data)
    );
  }

  private pushBannerEvent(
    event: ResourceBannerEvent,
    banner: ImageSEO,
    position: number,
    region: string,
    placement: string,
    pageContext: string
  ): void {
    if (typeof window === "undefined" || !banner.advertiserSlug) return;
    this.pushToDataLayer({
      event,
      ad_placement: placement,
      ad_region: region.toUpperCase(),
      advertiser_name: banner.advertiserName,
      advertiser_slug: banner.advertiserSlug,
      ad_position: position,
      ad_creative_id: `${banner.advertiserSlug}_${this.getQuarterYear()}`,
      page_context: pageContext,
      page_language: this.getPageLanguage()
    });
  }

  private pushResourceArticleEvent(
    event: ResourceBannerEvent,
    data: ResourceArticleEventData
  ): void {
    if (typeof window === "undefined" || !data.articleId) return;
    this.pushToDataLayer({
      event,
      ad_placement: data.placement,
      ad_region: environment.platformId.toUpperCase(),
      advertiser_name: RESOURCE_ARTICLE_ADVERTISER.name,
      advertiser_slug: RESOURCE_ARTICLE_ADVERTISER.slug,
      ad_position: data.position,
      ad_creative_id: `article_${data.articleId}`,
      page_context: data.pageContext,
      page_language: this.getPageLanguage()
    });
  }

  private pushToDataLayer(payload: object): void {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(payload);
  }

  private getPageLanguage(): string {
    const lang = this.siilaTranslationService.getCurrentLanguage();
    return lang === "pt" ? "pt-br" : lang;
  }

  private buildSpotCarouselPayload(
    event: string,
    data: SpotCarouselEventData
  ): object {
    return {
      event,
      ad_placement: "spot_carousel",
      page_context: data.pageContext,
      page_language: data.pageLanguage,
      property_name: data.propertyName,
      property_id: data.propertyId,
      spot_advertiser_name: data.spotAdvertiserName || "",
      spot_advertiser_slug: data.spotAdvertiserSlug || "",
      card_position: Number(data.cardPosition)
    };
  }

  private getQuarterYear(): string {
    const now = new Date();
    const quarter = Math.ceil((now.getMonth() + 1) / 3);
    return `q${quarter}_${now.getFullYear()}`;
  }

  private configTrackingID(trackingId: string) {
    try {
      const script1 = document.createElement("script");
      script1.async = true;
      script1.src = "https://www.googletagmanager.com/gtag/js?id=" + trackingId;
      document.head.appendChild(script1);

      const script2 = document.createElement("script");
      var user = JSON.parse(localStorage.getItem("user"));

      script2.innerHTML =
        `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '` +
        trackingId +
        `', {'send_page_view': true});
      `;

      document.head.appendChild(script2);
    } catch (ex) {
      console.error(`Error appending google analytics: ${trackingId}`);
      console.error(ex);
    }
  }
}
